import { format } from "date-fns";
import { nl } from "date-fns/locale";
import { EyeOff } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import type { NewProduct } from "@/hooks/useNewProducts";

interface NewProductsTableProps {
  products: NewProduct[] | undefined;
  isLoading: boolean;
  selectedIds: Set<string>;
  onSelectionChange: (id: string, selected: boolean) => void;
  onSelectAll: (selected: boolean) => void;
}

export default function NewProductsTable({
  products,
  isLoading,
  selectedIds,
  onSelectionChange,
  onSelectAll,
}: NewProductsTableProps) {
  const allSelected = products && products.length > 0 && products.every(p => selectedIds.has(p.id));
  const someSelected = products && products.some(p => selectedIds.has(p.id)) && !allSelected;

  const getVoorraadColor = (voorraad: number) => {
    if (voorraad <= 0) return "text-red-600 bg-red-50";
    if (voorraad < 10) return "text-orange-600 bg-orange-50";
    return "text-green-600 bg-green-50";
  };

  if (isLoading) {
    return (
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-12">
                <Checkbox disabled />
              </TableHead>
              <TableHead>Product Naam</TableHead>
              <TableHead>Artikelnummer</TableHead>
              <TableHead>Barcode</TableHead>
              <TableHead>Locatie</TableHead>
              <TableHead className="text-right">Voorraad</TableHead>
              <TableHead className="text-right">MOQ</TableHead>
              <TableHead className="text-right">Gewicht</TableHead>
              <TableHead>Eerste Detectie</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {Array.from({ length: 5 }).map((_, i) => (
              <TableRow key={i}>
                <TableCell><Skeleton className="h-4 w-4" /></TableCell>
                <TableCell><Skeleton className="h-4 w-48" /></TableCell>
                <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                <TableCell><Skeleton className="h-4 w-20" /></TableCell>
                <TableCell><Skeleton className="h-4 w-12" /></TableCell>
                <TableCell><Skeleton className="h-4 w-12" /></TableCell>
                <TableCell><Skeleton className="h-4 w-16" /></TableCell>
                <TableCell><Skeleton className="h-4 w-28" /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    );
  }

  if (!products || products.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center rounded-md border py-12 text-center">
        <div className="rounded-full bg-muted p-3">
          <EyeOff className="h-6 w-6 text-muted-foreground" />
        </div>
        <h3 className="mt-4 text-lg font-semibold">Geen nieuwe producten</h3>
        <p className="mt-2 text-sm text-muted-foreground">
          Er zijn momenteel geen nieuwe producten gedetecteerd in magazijn 2.
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-12">
              <Checkbox
                checked={allSelected}
                ref={(el) => {
                  if (el) {
                    (el as HTMLButtonElement & { indeterminate?: boolean }).indeterminate = someSelected;
                  }
                }}
                onCheckedChange={(checked) => onSelectAll(!!checked)}
              />
            </TableHead>
            <TableHead>Product Naam</TableHead>
            <TableHead>Artikelnummer</TableHead>
            <TableHead>Barcode</TableHead>
            <TableHead>Locatie</TableHead>
            <TableHead className="text-right">Voorraad</TableHead>
            <TableHead className="text-right">MOQ</TableHead>
            <TableHead className="text-right">Gewicht</TableHead>
            <TableHead>Eerste Detectie</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {products.map((product) => (
            <TableRow
              key={product.id}
              className={product.hidden ? "opacity-60" : undefined}
            >
              <TableCell>
                <Checkbox
                  checked={selectedIds.has(product.id)}
                  onCheckedChange={(checked) =>
                    onSelectionChange(product.id, !!checked)
                  }
                />
              </TableCell>
              <TableCell>
                <div className="flex flex-col">
                  <span className="font-medium">
                    {product.product_naam || "-"}
                  </span>
                  {product.eenheid && (
                    <span className="text-xs text-muted-foreground">
                      {product.eenheid}
                    </span>
                  )}
                  {product.hidden && (
                    <Badge variant="secondary" className="mt-1 w-fit">
                      <EyeOff className="mr-1 h-3 w-3" />
                      Verborgen
                    </Badge>
                  )}
                </div>
              </TableCell>
              <TableCell className="font-mono text-sm">
                {product.product_id}
              </TableCell>
              <TableCell className="font-mono text-sm">
                {product.bc || "-"}
              </TableCell>
              <TableCell>{product.locatie || "-"}</TableCell>
              <TableCell className="text-right">
                <Badge
                  variant="secondary"
                  className={getVoorraadColor(product.voorraad)}
                >
                  {product.voorraad}
                </Badge>
              </TableCell>
              <TableCell className="text-right">
                {product.moq || "-"}
              </TableCell>
              <TableCell className="text-right">
                {product.vrije_waarde_2
                  ? `${product.vrije_waarde_2} kg`
                  : "-"}
              </TableCell>
              <TableCell className="text-sm text-muted-foreground">
                {format(new Date(product.first_seen_at), "d MMM yyyy HH:mm", {
                  locale: nl,
                })}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
